import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Instagram,
  Facebook,
  Mail,
  ArrowUp,
  Sparkles,
  FileText,
} from "lucide-react";

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

export default function Footer() {
  const { i18n } = useTranslation();
  const year = useMemo(() => new Date().getFullYear(), []);
  const isVN = i18n.language?.startsWith("vi");

  const text = {
    brand: "OWL DREAM",
    tagline: isVN
      ? "Streetwear tối giản mang tinh thần nghệ thuật."
      : "Minimal streetwear shaped by art.",
    nav: isVN ? "Điều hướng" : "Navigation",
    policy: isVN ? "Chính sách" : "Policies",
    contact: isVN ? "Liên hệ" : "Contact",
    shop: isVN ? "Sản phẩm" : "Shop",
    drops: isVN ? "Drop" : "Drops",
    story: isVN ? "Câu chuyện" : "Story",
    policies: isVN ? "Chính sách" : "Policies",
    shipping: isVN ? "Vận chuyển" : "Shipping",
    payment: isVN ? "Thanh toán" : "Payment",
    returns: isVN ? "Đổi trả" : "Returns",
    privacy: isVN ? "Bảo mật" : "Privacy",
    terms: isVN ? "Điều khoản" : "Terms",
    top: isVN ? "Lên đầu" : "Top",
    rights: `© ${year} OWL DREAM`,
    note: isVN
      ? "Ít hơn nhưng đúng hơn."
      : "Less, but sharper.",
  };

  const backToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative overflow-hidden border-t border-brand-brown/10 bg-[#f6efe4] text-brand-brown">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(205,170,111,0.08),transparent_24%),radial-gradient(circle_at_bottom_right,rgba(43,31,26,0.05),transparent_28%)]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-10">
        <div className="rounded-[28px] border border-brand-brown/10 bg-white/62 shadow-[0_20px_60px_rgba(43,31,26,0.07)] backdrop-blur-xl">
          <div className="grid gap-0 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="border-b border-brand-brown/8 p-5 sm:p-6 lg:border-b-0 lg:border-r lg:p-7">
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-brown/10 bg-brand-ivory/70 text-brand-gold">
                  <Sparkles className="h-4 w-4" />
                </div>

                <div className="min-w-0">
                  <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-brand-brown">
                    {text.brand}
                  </p>
                  <p className="mt-1 text-sm text-brand-brown/60">
                    {text.tagline}
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <Link
                  to="/collection"
                  className="inline-flex h-10 items-center rounded-full border border-brand-brown/10 bg-white/70 px-4 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown/78 transition hover:border-brand-brown/20 hover:bg-white hover:text-brand-brown"
                >
                  {text.shop}
                </Link>

                <Link
                  to="/drops"
                  className="inline-flex h-10 items-center rounded-full border border-brand-brown/10 bg-white/70 px-4 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown/78 transition hover:border-brand-brown/20 hover:bg-white hover:text-brand-brown"
                >
                  {text.drops}
                </Link>

                <Link
                  to="/story"
                  className="inline-flex h-10 items-center rounded-full border border-brand-brown/10 bg-white/70 px-4 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown/78 transition hover:border-brand-brown/20 hover:bg-white hover:text-brand-brown"
                >
                  {text.story}
                </Link>

                <Link
                  to="/policies"
                  className="inline-flex h-10 items-center rounded-full border border-brand-brown/10 bg-white/70 px-4 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown/78 transition hover:border-brand-brown/20 hover:bg-white hover:text-brand-brown"
                >
                  {text.policies}
                </Link>
              </div>

              <div className="mt-5 rounded-[20px] border border-brand-brown/10 bg-brand-ivory/72 px-4 py-3">
                <p className="text-[10px] uppercase tracking-[0.18em] text-brand-brown/42">
                  OWL DREAM
                </p>
                <p className="mt-2 text-sm text-brand-brown/62">{text.note}</p>
              </div>
            </div>

            <div className="p-5 sm:p-6 lg:p-7">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <p className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/40">
                    <FileText className="h-4 w-4" />
                    {text.policy}
                  </p>

                  <div className="mt-4 flex flex-col gap-2">
                    <Link
                      to="/policies#shipping"
                      className="text-sm text-brand-brown/66 transition hover:text-brand-brown"
                    >
                      {text.shipping}
                    </Link>
                    <Link
                      to="/policies#payment"
                      className="text-sm text-brand-brown/66 transition hover:text-brand-brown"
                    >
                      {text.payment}
                    </Link>
                    <Link
                      to="/policies#returns"
                      className="text-sm text-brand-brown/66 transition hover:text-brand-brown"
                    >
                      {text.returns}
                    </Link>
                    <Link
                      to="/policies#privacy"
                      className="text-sm text-brand-brown/66 transition hover:text-brand-brown"
                    >
                      {text.privacy}
                    </Link>
                    <Link
                      to="/policies#terms"
                      className="text-sm text-brand-brown/66 transition hover:text-brand-brown"
                    >
                      {text.terms}
                    </Link>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/40">
                    {text.contact}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <a
                      href="https://www.instagram.com/owl_dream25"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-brown/10 bg-white/70 text-brand-brown/74 transition hover:border-brand-brown/20 hover:bg-white hover:text-brand-brown"
                      aria-label="Instagram"
                      title="Instagram"
                    >
                      <Instagram className="h-4 w-4" />
                    </a>

                    <a
                      href="https://www.facebook.com/owldream25"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-brown/10 bg-white/70 text-brand-brown/74 transition hover:border-brand-brown/20 hover:bg-white hover:text-brand-brown"
                      aria-label="Facebook"
                      title="Facebook"
                    >
                      <Facebook className="h-4 w-4" />
                    </a>

                    <a
                      href="mailto:contact@owldream.com"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-brown/10 bg-white/70 text-brand-brown/74 transition hover:border-brand-brown/20 hover:bg-white hover:text-brand-brown"
                      aria-label="Email"
                      title="Email"
                    >
                      <Mail className="h-4 w-4" />
                    </a>

                    <button
                      type="button"
                      onClick={backToTop}
                      className="inline-flex h-10 items-center gap-2 rounded-full border border-brand-brown/10 bg-white/70 px-4 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown/72 transition hover:border-brand-brown/20 hover:bg-white hover:text-brand-brown"
                    >
                      {text.top}
                      <ArrowUp className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-5 border-t border-brand-brown/8 pt-4">
                <p className="text-[10px] uppercase tracking-[0.16em] text-brand-brown/40">
                  {text.rights}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}